import { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
    useActions,
    useTranslation,
    useTypedSelector,
} from '../../../../../../../hooks'
import LoadingStatisticsPage from '../../../../../loading/LoadingStatisticsPage'
import TotalCases from '../../../../../partners/statistics/components/TotalCases'
import AnonymousConfidential from '../../../../../partners/statistics/components/AnonymousConfidential'
import ActionStats from '../../../../../partners/statistics/components/ActionStats'
import PieChartCategories from '../../../../../partners/statistics/components/PieChartCategories'
import StackedBarCases from '../../../../../partners/statistics/components/StackedBarCases'
import {
    Section,
    CasesChartSection,
    FlexColumn,
    CasesChartCard,
    BigCard,
    Title,
} from '../../../../../partners/statistics/StatisticsPage'
import { HeadingSecondary } from '../../../../../../styled/typography'

const Container = styled.div``

const ToolbarWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 1.5rem 0 1.3rem 0;
`

const Toolbar = styled.div`
    display: flex;
`

const ToolWrap = styled.div`
    display: flex;
    margin: 0 1rem;
`

const Select = styled.select`
    border: none;
    background-color: transparent;
    font-size: 1.6rem;
    cursor: pointer;
    &:focus {
        outline: none;
    }
    option {
        font-size: 1.2rem;
        cursor: pointer;
    }
`

interface PartnerOrganizationStatisticsFrameProps {
    partnerId: number
    organizationId: number
}

const PartnerOrganizationStatisticsFrame = ({
    partnerId,
    organizationId,
}: PartnerOrganizationStatisticsFrameProps) => {
    const translation = useTranslation()

    const statisticsState = useTypedSelector(
        (state) => state.partnerOrgStatistics
    )

    const [selectedYear, setSelectedYear] = useState(
        new Date().getFullYear().toString()
    )

    const { getPartnerOrgStatistics } = useActions()

    useEffect(() => {
        getPartnerOrgStatistics(partnerId, organizationId, selectedYear)
        // eslint-disable-next-line
    }, [partnerId, organizationId, selectedYear])

    return (
        <Container>
            {statisticsState.type === 'loading' && <LoadingStatisticsPage />}
            {statisticsState.type === 'loaded' && (
                <>
                    <ToolbarWrapper>
                        <HeadingSecondary style={{ fontSize: '2rem' }}>
                            {translation.statisticsPage.overview}
                        </HeadingSecondary>
                        <Toolbar>
                            <ToolWrap>
                                <Select
                                    value={selectedYear}
                                    onChange={(event) =>
                                        setSelectedYear(event.target.value)
                                    }
                                >
                                    {statisticsState.statistics.availableYears.map(
                                        (year: string) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        )
                                    )}
                                </Select>
                            </ToolWrap>
                        </Toolbar>
                    </ToolbarWrapper>
                    <Section>
                        <FlexColumn>
                            <TotalCases
                                totalCases={
                                    statisticsState.statistics.totalCases
                                }
                            />
                            <AnonymousConfidential
                                caseTypeSplit={
                                    statisticsState.statistics.caseTypeSplit
                                }
                                hasCases={
                                    !!statisticsState.statistics.totalCases.all
                                }
                            />
                        </FlexColumn>
                        <ActionStats
                            averages={statisticsState.statistics.averages}
                        />
                        <BigCard padding={'0 0.2rem'}>
                            <Title margin={'1.5rem 0 0 1.5rem'}>
                                {
                                    translation.statisticsPage
                                        .distributionOfCategories
                                }
                            </Title>
                            <PieChartCategories
                                categories={
                                    statisticsState.statistics.categories
                                }
                            />
                        </BigCard>
                    </Section>
                    <CasesChartSection>
                        <CasesChartCard>
                            <Title margin={'1.5rem 0 1.5rem 1.5rem'}>
                                {translation.statisticsPage.casesByMonth}
                            </Title>
                            <StackedBarCases
                                months={statisticsState.statistics.months}
                            />
                        </CasesChartCard>
                    </CasesChartSection>
                </>
            )}
        </Container>
    )
}

export default PartnerOrganizationStatisticsFrame
